import { kieli } from "./kieli";
/**
 * nayttonimi.ts — koodiarvo ei ole näyttönimi.
 *
 * Kannan koodiarvot ovat ASCII-muotoisia tarkoituksella (`lahdeaukko`,
 * `pysayttaa`, `kayttoonotto`): avain pysyy, CHECK-rajoite pitää, haku
 * löytää, eikä tunnisteessa ole merkistöriippuvuutta. Ne ovat väärin
 * vain ruudulla. Mitattu 7.9.2026: koodiarvo renderöityi sellaisenaan
 * noin 25 kohdassa 15 näkymässä, ja näyttönimiä oli seitsemänä
 * erillisenä paikallisena karttana (Hub, Kalusto, LaiteSelailu, Kohteet,
 * Tarjous, Tarjousasetukset, Puutteet). Sama vika korjattuna yksi
 * kerrallaan on se tapa jolla sama rivi kirjoitetaan tiekartalle
 * kolmesti — siksi yksi kartta ja yksi apufunktio.
 *
 * Kartta asuu arvon vieressä: näyttönimi JA lyhyt selite, koska
 * «lähdeaukko» ja «ristiriita» eivät ole termejä joita kentällä luetaan
 * samalla tavalla kuin meillä (punchin A/B/C opetti: selite valinnan
 * viereen, ei erilliseen ohjeeseen).
 *
 * `lahde` nimeää migraation ja sarakkeen jonka CHECK-rajoite on arvojen
 * totuus. `api/src/nayttonimi.test.ts` lukee sen ja kaatuu jos kartta ja
 * rajoite eroavat — kumpaan suuntaan tahansa. Perhe ilman `lahde`a
 * (arvot koodissa tai vapaassa sarakkeessa) ei ole tarkistuksen
 * piirissä, ja se lukee tässä eikä ole piilotettu.
 *
 * Tuntematon arvo palautetaan sellaisenaan: koodi ruudulla on näkyvä
 * vika, keksitty nimi ei.
 */

import { KOHDE_TILA } from "./api";

/** T439: `en` = englanninkielinen nimi (koneen ehdotus 7.10.2026, tarkistus T440). Puuttuva → suomi. */
export interface Nimi { nimi: string; selite?: string; en?: string; selite_en?: string }
export interface Perhe {
  /** Migraatio ja sarake jonka CHECK on totuus — tai null jos arvot eivät ole rajoitteessa. */
  lahde: { migraatio: string; sarake: string } | null;
  arvot: Record<string, Nimi>;
}

export const NAYTTONIMET = {
  puute_tyyppi: {
    lahde: { migraatio: "234_puute_huomio.sql", sarake: "tyyppi" },
    arvot: {
      punch:           { nimi: "Punch",           selite: "tarkastuslöydös linjalla, korjataan ja kuitataan",
                         en: "Punch item", selite_en: "inspection finding on a line, fixed and signed off" },
      lahdeaukko:      { nimi: "Lähdeaukko",      selite: "lähtötieto puuttuu — isometri, suunnitelma tai DN ei ole tiedossa",
                         en: "Missing input", selite_en: "input data missing — isometric, plan or DN unknown" },
      ristiriita:      { nimi: "Ristiriita",      selite: "kaksi lähdettä sanovat eri asiaa — esim. P&ID:n DN vs toteutettu",
                         en: "Conflict", selite_en: "two sources disagree — e.g. P&ID DN vs as-built" },
      dokumenttivelka: { nimi: "Dokumentointivelka", selite: "työ on tehty mutta luovutusaineisto puuttuu",
                         en: "Documentation debt", selite_en: "work is done but handover documents are missing" },
      vika:            { nimi: "Vika",            selite: "havaittu poikkeama joka ei ole punch — esim. pätevyys tai perehdytys",
                         en: "Defect", selite_en: "observed deviation that is not a punch item — e.g. qualification or induction" },
      // 234 (T565, Toni 13.10.2026): tieto hitsille, ei työjonossa — esim. tuonnin tarran muotokorjaus.
      huomio:          { nimi: "Huomio",          selite: "tieto, ei tehtävä — esim. tuonnissa korjattu tarran muoto (alkuperäinen → tallennettu)",
                         en: "Note", selite_en: "information, not a task — e.g. sticker format corrected on import (original → stored)" },
    },
  },
  puute_vaikutus: {
    lahde: { migraatio: "017_puute.sql", sarake: "vaikutus" },
    arvot: {
      pysayttaa: { nimi: "Pysäyttää", selite: "estää työn jatkamisen kunnes korjattu",
                   en: "Stops work", selite_en: "work cannot continue until fixed" },
      hidastaa:  { nimi: "Hidastaa",  selite: "työ jatkuu, mutta puute on kirjattava ja hoidettava",
                   en: "Slows work", selite_en: "work continues, but the issue must be recorded and handled" },
      ei_esta:   { nimi: "Ei estä",   selite: "tiedoksi — ei vaikuta työn kulkuun",
                   en: "No impact", selite_en: "for information — does not affect the work" },
    },
  },
  puute_tila: {
    lahde: { migraatio: "017_puute.sql", sarake: "tila" },
    arvot: {
      avoin:     { nimi: "Avoin", en: "Open" },
      kaynnissa: { nimi: "Käynnissä", selite: "joku on ottanut hoitaakseen", en: "In progress", selite_en: "someone has taken it on" },
      ratkaistu: { nimi: "Ratkaistu", selite: "syy poistunut", en: "Resolved", selite_en: "the cause is gone" },
      hylatty:   { nimi: "Hylätty",   selite: "ei aiheellinen — automaatti voi nostaa uudelleen jos syy on yhä totta",
                   en: "Rejected", selite_en: "not valid — automation may raise it again if the cause still exists" },
    },
  },
  osaaminen_saanto: {
    lahde: { migraatio: "069_osaaminen.sql", sarake: "saanto" },
    arvot: {
      nimitys:   { nimi: "Nimitys",   selite: "rivi on nimetty mutta sitä ei ole — esim. linja P&ID:ssä muttei kannassa" },
      kattavuus: { nimi: "Kattavuus", selite: "vaadittu osuus ei täyty — esim. NDT-% alle PED-vaatimuksen" },
      ketju:     { nimi: "Ketju",     selite: "ketjun lenkki puuttuu — esim. sulatus ilman ainestodistusta" },
    },
  },
  havainnointisaanto: {
    lahde: null,
    arvot: {
      hitsaaja_tuntematon:      { nimi: "Hitsaaja tuntematon",       selite: "hitsin hitsaajaa ei ole rekisterissä" },
      hitsisuunnitelma_puuttuu: { nimi: "Hitsisuunnitelma puuttuu",  selite: "linjalla ei ole suunniteltuja hitsejä" },
      isometri_puuttuu:         { nimi: "Isometri puuttuu" },
      kalusto_katsastus:        { nimi: "Katsastus vanhenee" },
      korjaus_orpo:             { nimi: "Korjaus ilman alkuperäistä", selite: "korjaushitsi jonka alkuperäistä ei ole" },
      koulutus_vanhenee:        { nimi: "Koulutus vanhenee" },
      materiaali_puuttuu:       { nimi: "Materiaali puuttuu",        selite: "hitsin osalta puuttuu materiaali tai sulatus" },
      paiva_jasentymaton:       { nimi: "Päivä jäsentymätön",        selite: "lähteen päivämäärää ei voitu lukea" },
      patevyys_kesken_tyon:     { nimi: "Pätevyys umpeutuu kesken työn" },
      patevyys_vanhenee:        { nimi: "Pätevyys vanhenee" },
      sulatus_puuttuu:          { nimi: "Sulatus ilman ainestodistusta" },
    },
  },
  /* 10.10.2026: Puutteiden kattavuusrivin `saanto` on tunnisteen ENSIMMÄINEN osa (099: split_part(tunniste, '_', 1)), ei
     koko säännön avain — kartan `havainnointisaanto` ei osunut yhteenkään, ja rivi näytti raakoja koodeja kummallakin
     kielellä (koko valikon kielipyyhkäisy demossa: «sulatus»). Mitattu 10.10.: demo, protot, aaltoflow, pekilo. */
  havainnointisaanto_lyhyt: {
    lahde: null,
    arvot: {
      hitsaaja:         { nimi: "Hitsaaja tuntematon", en: "Unknown welder" },
      hitsisuunnitelma: { nimi: "Hitsisuunnitelma puuttuu", en: "Weld plan missing" },
      isometri:         { nimi: "Isometri puuttuu", en: "Isometric missing" },
      materiaali:       { nimi: "Materiaali puuttuu", en: "Material missing" },
      sulatus:          { nimi: "Sulatus ilman ainestodistusta", en: "Heat number without material certificate" },
      paiva:            { nimi: "Päivä jäsentymätön", en: "Date not parsed" },
      korjaus:          { nimi: "Korjaus ilman alkuperäistä", en: "Repair without original" },
      kalusto:          { nimi: "Kalusto", en: "Equipment" },
      painekoe:         { nimi: "Painekokeen välineet", en: "Pressure test instruments" },
      patevyys:         { nimi: "Pätevyys", en: "Qualification" },
      perehdytys:       { nimi: "Perehdytys puuttuu", en: "Induction missing" },
      henkilo:          { nimi: "Tunnistamaton nimi", en: "Unidentified name" },
      iv:               { nimi: "IV-mittaus", en: "HVAC measurement" },
      koulutus:         { nimi: "Koulutus vanhenee", en: "Training expires" },
      ped:              { nimi: "PED-luokka", en: "PED category" },
    },
  },
  kohde_taulu: {
    lahde: null,
    arvot: {
      henkilo:        { nimi: "Henkilö", en: "Person" },
      hitsi:          { nimi: "Hitsi", en: "Weld" },
      linja:          { nimi: "Linja", en: "Line" },
      kohde:          { nimi: "Kohde", en: "Project" },
      kalusto:        { nimi: "Kalusto", en: "Equipment" },
      tyopaketti:     { nimi: "Työpaketti", en: "Work package" },
      laite:          { nimi: "Laite", en: "Device" },
      resurssi:       { nimi: "Resurssi", en: "Resource" },
      patevyys:       { nimi: "Pätevyys", en: "Qualification" },
      ainestodistus:  { nimi: "Ainestodistus", en: "Material certificate" },
      varasto_nimike: { nimi: "Varastonimike", en: "Stock item" },
      punch:          { nimi: "Punch", en: "Punch item" },
      linjatarkastus: { nimi: "Linjatarkastus", en: "Line inspection" },
    },
  },
  kohde_tila: {
    lahde: { migraatio: "088_kohde_elinkaari.sql", sarake: "tila" },
    arvot: KOHDE_TILA as Record<string, Nimi>,
  },
  kohde_laji: {
    lahde: { migraatio: "001_init.sql", sarake: "kohde.laji" },
    arvot: {
      projekti: { nimi: "Projekti", selite: "alkaa ja päättyy — luovutus" },
      jatkuva:  { nimi: "Jatkuva",  selite: "ei päättymispäivää — esim. kunnossapito" },
    },
  },
  linja_tila: {
    lahde: { migraatio: "018_hitsausloki.sql", sarake: "linja.tila" },
    arvot: {
      kesken:      { nimi: "Kesken" },
      valmis:      { nimi: "Valmis" },
      painekoe_ok: { nimi: "Painekoe OK" },
      luovutettu:  { nimi: "Luovutettu" },
    },
  },
  hitsi_tila: {
    lahde: { migraatio: "018_hitsausloki.sql", sarake: "hitsi.tila" },
    arvot: {
      kirjattu:    { nimi: "Kirjattu" },
      tarkastettu: { nimi: "Tarkastettu" },
      hyvaksytty:  { nimi: "Hyväksytty" },
      hylatty:     { nimi: "Hylätty" },
    },
  },
  hitsi_vaihe: {
    lahde: null,
    arvot: {
      suunniteltu: { nimi: "Suunniteltu", selite: "isometristä esitäytetty, ei vielä hitsattu" },
      hitsattu:    { nimi: "Hitsattu" },
    },
  },
  hitsi_lahde: {
    lahde: { migraatio: "018_hitsausloki.sql", sarake: "hitsi.lahde" },
    arvot: {
      kaytto:  { nimi: "Kenttäkirjaus", selite: "kirjattu järjestelmässä" },
      tuonti:  { nimi: "Tuonti",        selite: "tuotu tiedostosta" },
      korjaus: { nimi: "Korjaus",       selite: "korjattu jälkikäteen" },
    },
  },
  isantajarjestelma: {
    lahde: { migraatio: "095_hitsin_isantajarjestelma.sql", sarake: "isantajarjestelma" },
    arvot: {
      sampo:      { nimi: "Sampo Industrial", selite: "rivi elää tässä järjestelmässä" },
      sharepoint: { nimi: "SharePoint", selite: "rivi elää SharePointissa; tuonti päivittää" },
    },
  },
  istunto_laji: {
    lahde: { migraatio: "018_hitsausloki.sql", sarake: "hitsi.laji" },
    arvot: {
      tuotanto: { nimi: "Tuotanto" },
      korjaus:  { nimi: "Korjaus" },
      uusinta:  { nimi: "Uusinta" },
    },
  },
  ndt_tulos: {
    lahde: { migraatio: "018_hitsausloki.sql", sarake: "tarkastus.tulos" },
    arvot: {
      // 17.10.2026 (Toni klo 17.45): englanti — kopiokävely edb5339 löysi «VT: hyväksytty» kenttätyön Selailusta lt- ja en-lukijalle.
      kesken:     { nimi: "Kesken",     en: "In progress" },
      hyvaksytty: { nimi: "Hyväksytty", en: "Accepted" },
      hylatty:    { nimi: "Hylätty",    en: "Rejected" },
    },
  },
  katsastus_tulos: {
    lahde: { migraatio: "026_kalusto.sql", sarake: "tulos" },
    arvot: {
      hyvaksytty: { nimi: "Hyväksytty" },
      ehdollinen: { nimi: "Ehdollinen", selite: "hyväksytty korjausehdolla" },
      hylatty:    { nimi: "Hylätty" },
    },
  },
  asiakas_poisto_syy: {
    lahde: { migraatio: "118_asiakas_poisto.sql", sarake: "poisto_syy" },
    arvot: {
      virheellinen:   { nimi: "Virheellinen", selite: "Luotiin vahingossa tai väärillä tiedoilla" },
      kaksoiskappale: { nimi: "Kaksoiskappale", selite: "Sama asiakas on jo rekisterissä toisella rivillä" },
      paattynyt:      { nimi: "Päättynyt asiakkuus" },
      muu:            { nimi: "Muu", selite: "Peruste sanoin on pakollinen" },
    },
  },
  kalusto_poisto_syy: {
    lahde: { migraatio: "116_kalusto_poisto.sql", sarake: "poisto_syy" },
    arvot: {
      myyty:                  { nimi: "Myyty", en: "Sold" },
      romutettu:              { nimi: "Romutettu", en: "Scrapped" },
      kadonnut:               { nimi: "Kadonnut", en: "Lost" },
      palautettu_vuokraamoon: { nimi: "Palautettu vuokraamoon", en: "Returned to rental" },
      siirretty:              { nimi: "Siirretty", selite: "Toiseen yksikköön tai yhtiöön", en: "Transferred", selite_en: "To another unit or company" },
      muu:                    { nimi: "Muu", selite: "Peruste sanoin on pakollinen", en: "Other", selite_en: "A written reason is required" },
    },
  },
  kalusto_laji: {
    lahde: { migraatio: "164_kalusto_rekisteri.sql", sarake: "laji" },
    arvot: {
      tyokalu:        { nimi: "Työkalu", selite: "Kalustopohjan välilehti «Työkalut»", en: "Tool" },
      nostoapuvaline: { nimi: "Nostoapuväline", selite: "Kantavuus (WLL) kirjataan vain näille", en: "Lifting accessory", selite_en: "Working load limit (WLL) recorded only for these" },
      henkilovaljaat: { nimi: "Henkilövaljaat", selite: "Aina yksilö: ei tyyppiä eikä määrää", en: "Safety harness", selite_en: "Always an individual item: no type or quantity" },
    },
  },
  katsastus_tulos_valinta: {
    lahde: null,
    arvot: {
      hyvaksytty: { nimi: "Hyväksytty" },
      ehdollinen: { nimi: "Ehdollinen", selite: "Syy pakollinen: mikä ehto ja mihin asti" },
      hylatty:    { nimi: "Hylätty", selite: "Laite merkitään pois käytöstä" },
    },
  },
  kalusto_kunto: {
    lahde: { migraatio: "164_kalusto_rekisteri.sql", sarake: "kunto" },
    arvot: {
      hyva:        { nimi: "Hyvä", en: "Good" },
      tyydyttava:  { nimi: "Tyydyttävä", en: "Fair" },
      huono:       { nimi: "Huono", en: "Poor" },
      poistettava: { nimi: "Poistettava", selite: "Kunto ei ole poisto: laite on inventaariossa kunnes se poistetaan perusteineen",
                     en: "To be removed", selite_en: "Condition is not removal: the item stays in the inventory until removed with a reason" },
    },
  },
  kalusto_qr_korvaus_syy: {
    lahde: { migraatio: "172_kalusto_qr.sql", sarake: "korvaus_syy" },
    arvot: {
      tarra_vaurioitunut: { nimi: "Tarra vaurioitunut", selite: "Uusi tarra samaan laitteeseen", en: "Tag damaged", selite_en: "New tag on the same item" },
      tarra_kadonnut:     { nimi: "Tarra kadonnut", en: "Tag lost" },
      vaara_laite:        { nimi: "Väärä laite", selite: "Tarra liimattiin eri laitteeseen kuin se sidottiin — vanha numero ei avaa kumpaakaan",
                            en: "Wrong item", selite_en: "The tag was stuck on a different item than it was linked to — the old number opens neither" },
      muu:                { nimi: "Muu", selite: "Peruste sanoin on pakollinen", en: "Other", selite_en: "A written reason is required" },
    },
  },
  kalusto_sijainti: {
    lahde: null,
    arvot: {
      varasto:    { nimi: "Varasto", en: "Warehouse" },
      kohde:      { nimi: "Kohde", en: "Project" },
      auto:       { nimi: "Auto", selite: "Rekisterinumerolla", en: "Vehicle", selite_en: "By registration number" },
      toimipiste: { nimi: "Toimipiste", selite: "Kiinteä toimipiste", en: "Office", selite_en: "Fixed premises" },
      // 173: haltija. Ei valittavissa lomakkeissa — haltuun otetaan skannaamalla.
      henkilo:    { nimi: "Hallussa", selite: "Otettu haltuun skannaamalla; palautus tarran kautta", en: "Held by", selite_en: "Taken by scanning; returned via the tag" },
    },
  },
  kalusto_tila: {
    lahde: null,
    arvot: {
      voimassa:       { nimi: "Voimassa", en: "Valid" },
      vanhenee:       { nimi: "Vanhenee pian", en: "Expiring soon" },
      vanhentunut:    { nimi: "Vanhentunut", en: "Expired" },
      ei_katsastusta: { nimi: "Ei katsastusta", en: "No inspection" },
      pois_kaytosta:  { nimi: "Pois käytöstä", selite: "Viimeisin tarkastus hylätty — uusi hyväksytty tarkastus palauttaa käyttöön",
                        en: "Out of service", selite_en: "Latest inspection failed — a new passed inspection returns it to service" },
    },
  },
  patevyys_tila: {
    lahde: null,
    arvot: {
      voimassa:     { nimi: "Voimassa", en: "Valid" },
      varoitus:     { nimi: "Vanhenee",     selite: "umpeutuu muistutusajan sisällä", en: "Expiring" },
      kriittinen:   { nimi: "Vanhenee pian", selite: "umpeutuu alle kuukaudessa", en: "Expiring soon" },
      vanhentunut:  { nimi: "Vanhentunut",  selite: "henkilö ei ole pätevä", en: "Expired" },
      "ei vanhene": { nimi: "Ei vanhene", en: "Does not expire" },
    },
  },
  varasto_laji: {
    lahde: null,   // 047: arvot vain kommentissa, ei CHECKiä
    arvot: {
      saapuminen:  { nimi: "Saapuminen",  selite: "+ saldoon" },
      otto:        { nimi: "Otto",        selite: "− saldosta, kohdistuu työpakettiin" },
      inventaario: { nimi: "Inventaario", selite: "oikaisu laskettuun saldoon" },
      korjaus:     { nimi: "Korjaus",     selite: "± käsin" },
    },
  },
  allokaatio_laji: {
    lahde: { migraatio: "001_init.sql", sarake: "allokaatio.laji" },
    arvot: {
      tyo:        { nimi: "Työ" },
      varallaolo: { nimi: "Varallaolo" },
      matka:      { nimi: "Matka" },
    },
  },
  rooli: {
    lahde: null,
    arvot: {
      paakayttaja:            { nimi: "Pääkäyttäjä", en: "Administrator" },
      suunnittelija:          { nimi: "Suunnittelija", en: "Planner" },
      tyonjohtaja:            { nimi: "Työnjohtaja", en: "Supervisor" },
      kenttatekija:           { nimi: "Kenttätekijä", en: "Field worker" },
      hitsauksen_tyonjohtaja: { nimi: "Hitsauksen työnjohtaja", en: "Welding supervisor" },
      lukija:                 { nimi: "Lukija", en: "Reader" },
    },
  },
  tyosuhde: {
    lahde: { migraatio: "073_organisaatio.sql", sarake: "tyosuhde" },
    arvot: {
      oma:          { nimi: "Oma", en: "Own employee" },
      vuokra:       { nimi: "Vuokra", en: "Hired" },
      alihankinta:  { nimi: "Alihankinta", en: "Subcontracted" },
      ulkopuolinen: { nimi: "Ulkopuolinen", en: "External" },
    },
  },
  /* 10.10.2026 (pääChatin toimeksianto Tonin välittämänä, video 1): osaamisluokan ja pätevyystyypin nimi on talon oma
     vapaa teksti kannassa, joten avain on suomenkielinen nimi sellaisenaan. Kartassa ovat siemenen ja instansseissa
     mitatut yleiset nimet (lukeva mittaus 10.10.: demo, protot, aaltoflow, pekilo); talon oma nimi (esim. työmaan
     perehdytys) näytetään sellaisenaan, koska se on dataa eikä käyttöliittymää. Englanti on koneen ehdotus (T440). */
  /* 10.10.2026 (pääChatin toimeksianto Tonin välittämänä): puute.vastuu on vapaa teksti, mutta havainnointi kirjoittaa siihen
     kaksi koodiarvoa. Mitattu 10.10.: demo tyonjohto 4 671 · suunnittelu 59; protot tyonjohto 35; pekilo tyonjohto 5 436 ·
     suunnittelu 809 + 5 käsin kirjoitettua. Käsin kirjoitettu näytetään sellaisenaan. */
  puute_vastuu: {
    lahde: null,
    arvot: {
      tyonjohto:   { nimi: "Työnjohto", en: "Supervision" },
      suunnittelu: { nimi: "Suunnittelu", en: "Design" },
    },
  },
  osaamisluokka_nimi: {
    lahde: null,
    arvot: {
      "Hitsaaja":            { nimi: "Hitsaaja", en: "Welder" },
      "Asentaja":            { nimi: "Asentaja", en: "Fitter" },
      "Asentaja (hitsaus)":  { nimi: "Asentaja (hitsaus)", en: "Fitter (welding)" },
      "Putkiasentaja":       { nimi: "Putkiasentaja", en: "Pipe fitter" },
      "Automaatioasentaja":  { nimi: "Automaatioasentaja", en: "Automation technician" },
      "Sähköasentaja":       { nimi: "Sähköasentaja", en: "Electrician" },
      "Eristäjä":            { nimi: "Eristäjä", en: "Insulator" },
      "IV-asentaja":         { nimi: "IV-asentaja", en: "HVAC installer" },
      "IV-mittaaja":         { nimi: "IV-mittaaja", en: "HVAC commissioning technician" },
      "Työnjohtaja":         { nimi: "Työnjohtaja", en: "Supervisor" },
      "Projektipäällikkö":   { nimi: "Projektipäällikkö", en: "Project manager" },
      "Liiketoimintajohtaja": { nimi: "Liiketoimintajohtaja", en: "Business director" },
      "Työsuunnittelija":    { nimi: "Työsuunnittelija", en: "Work planner" },
      "Materiaalivastaava":  { nimi: "Materiaalivastaava", en: "Materials coordinator" },
      "Dokumentointi":       { nimi: "Dokumentointi", en: "Documentation" },
    },
  },
  patevyystyyppi_nimi: {
    lahde: null,
    arvot: {
      "Hitsaajan pätevyys EN ISO 9606": { nimi: "Hitsaajan pätevyys EN ISO 9606", en: "Welder qualification EN ISO 9606" },
      "Tulityökortti":                  { nimi: "Tulityökortti", en: "Hot work card" },
      "Työturvallisuuskortti":          { nimi: "Työturvallisuuskortti", en: "Occupational safety card" },
      "Sähkötyöturvallisuus SFS 6002":  { nimi: "Sähkötyöturvallisuus SFS 6002", en: "Electrical safety SFS 6002" },
      "Ensiapu EA1":                    { nimi: "Ensiapu EA1", en: "First aid EA1" },
      "Mekaaninen hitsausoperaattori":  { nimi: "Mekaaninen hitsausoperaattori", en: "Mechanised welding operator" },
      "Vesityökortti":                  { nimi: "Vesityökortti", en: "Water work card" },
    },
  },
  kayttaja_laji: {
    lahde: { migraatio: "106_tunnus_henkilosta.sql", sarake: "laji" },
    arvot: {
      henkilo:      { nimi: "Henkilö" },
      jarjestelma:  { nimi: "Järjestelmä" },
      ulkopuolinen: { nimi: "Ulkopuolinen", selite: "esim. tilaajan edustaja — vain lukija" },
    },
  },
  laite_laji: {
    lahde: { migraatio: "085_laiteasennus.sql", sarake: "laji" },
    arvot: {
      asennus:      { nimi: "Asennus" },
      kayttoonotto: { nimi: "Käyttöönotto" },
    },
  },
  piiri: {
    lahde: { migraatio: "085_laiteasennus.sql", sarake: "piiri" },
    arvot: {
      primaari:   { nimi: "Primaari" },
      sekundaari: { nimi: "Sekundaari" },
    },
  },
  vaiheketju_lahde: {
    lahde: { migraatio: "085_laiteasennus.sql", sarake: "lahde" },
    arvot: {
      ehdotus:  { nimi: "Ehdotus" },
      asiakas:  { nimi: "Asiakas" },
      manuaali: { nimi: "Manuaali" },
    },
  },
  tarjous_tila: {
    lahde: { migraatio: "027_tarjous.sql", sarake: "tila" },
    arvot: {
      luonnos:    { nimi: "Luonnos" },
      annettu:    { nimi: "Annettu" },
      hyvaksytty: { nimi: "Hyväksytty" },
      havitty:    { nimi: "Hävitty" },
      peruttu:    { nimi: "Peruttu" },
    },
  },
  kustannus_laji: {
    lahde: { migraatio: "027_tarjous.sql", sarake: "laji" },
    arvot: {
      materiaali:    { nimi: "Materiaali" },
      kalusto:       { nimi: "Kalusto" },
      ndt:           { nimi: "NDT" },
      koeponnistus:  { nimi: "Koeponnistus" },
      telineet:      { nimi: "Telineet" },
      dokumentointi: { nimi: "Dokumentointi" },
      vuokratyo:     { nimi: "Vuokratyö" },
      paivaraha:     { nimi: "Päiväraha" },
      majoitus:      { nimi: "Majoitus" },
      matkat:        { nimi: "Matkat" },
      muu:           { nimi: "Muu" },
    },
  },
  tes_laji: {
    lahde: { migraatio: "029_tarjouslaskenta_vaihe2.sql", sarake: "laji" },
    arvot: {
      ilta:      { nimi: "Iltalisä" },
      yo:        { nimi: "Yölisä" },
      lauantai:  { nimi: "Lauantailisä" },
      sunnuntai: { nimi: "Sunnuntailisä" },
      ylityo50:  { nimi: "Ylityö 50 %" },
      ylityo100: { nimi: "Ylityö 100 %" },
      arkipyha:  { nimi: "Arkipyhälisä" },
    },
  },
  paivaraha: {
    lahde: null,
    arvot: {
      ei:   { nimi: "Ei" },
      osa:  { nimi: "Osapäiväraha" },
      koko: { nimi: "Kokopäiväraha" },
    },
  },
  saatavuus: {
    lahde: null,
    arvot: {
      vapaa:        { nimi: "Vapaa" },
      jo_kohteella: { nimi: "Jo kohteella" },
      varattu:      { nimi: "Varattu" },
      poissa:       { nimi: "Poissa" },
    },
  },
  rotaatio_tila: {
    lahde: { migraatio: "043_tyovuoro_rotaatio.sql", sarake: "tila" },
    arvot: {
      paalla: { nimi: "Päällä" },
      pois:   { nimi: "Pois" },
    },
  },
} satisfies Record<string, Perhe>;

export type NimiPerhe = keyof typeof NAYTTONIMET;

/** Näyttönimi koodiarvolle. Tuntematon arvo palautuu sellaisenaan —
 *  koodi ruudulla on näkyvä vika, keksitty nimi ei. */
export function nayttonimi(perhe: NimiPerhe, arvo: string | null | undefined): string {
  if (arvo === null || arvo === undefined || arvo === "") return "";
  const n = (NAYTTONIMET[perhe].arvot as Record<string, Nimi>)[arvo];
  // T439: muu kieli kuin suomi lukee `en`-kentän jos se on; tuntematon arvo palautuu yhä sellaisenaan.
  if (n && kieli() !== "fi" && n.en) return n.en;
  return n?.nimi ?? arvo;
}

/** Lyhyt selite valinnan viereen (title-attribuutti tai option-teksti). */
export function selite(perhe: NimiPerhe, arvo: string | null | undefined): string | undefined {
  if (!arvo) return undefined;
  const n = (NAYTTONIMET[perhe].arvot as Record<string, Nimi>)[arvo];
  // T439: muulla kielellä vain käännetty selite; suomenkielistä selitettä ei näytetä englanninkieliselle.
  return kieli() === "fi" ? n?.selite : n?.selite_en;
}

/** Näyttönimi lauseen sisään: pieni alkukirjain («lähdeaukko 1709»). */
export function nayttonimiPieni(perhe: NimiPerhe, arvo: string | null | undefined): string {
  const n = nayttonimi(perhe, arvo);
  const r = (NAYTTONIMET[perhe].arvot as Record<string, Nimi>)[arvo ?? ""];
  return n && (n === r?.nimi || n === r?.en) && !/^[A-Z]{2}/.test(n)
    ? n.charAt(0).toLowerCase() + n.slice(1) : n;
}

/** Perheen arvot valintalistaa varten, kartan järjestyksessä. */
export function vaihtoehdot(perhe: NimiPerhe): { arvo: string; nimi: string; selite?: string }[] {
  return Object.entries(NAYTTONIMET[perhe].arvot as Record<string, Nimi>)
    .map(([arvo, n]) => {
      // T439: nimi ja selite kielen mukaan; puuttuva englanninkielinen selite jätetään pois eikä näytetä suomeksi.
      const nimi = kieli() !== "fi" && n.en ? n.en : n.nimi;
      const s = kieli() === "fi" ? n.selite : n.selite_en;
      return { arvo, nimi, ...(s ? { selite: s } : {}) };
    });
}
